// Starter JHA procedures by task type. These are STARTING POINTS — review and
// tailor each with your competent person; they are not a substitute for a
// qualified job hazard analysis. Each row is { step, hazard, control }.
export const JHA_TYPES = [
  'Excavation', 'Trenching', 'Potholing', 'Directional Drilling', 'Conduit Installation',
  'Wire Pulling', 'Energized Electrical Exposure', 'De-energized Electrical Work', 'Lockout/Tagout',
  'Traffic Control', 'Street Lighting', 'Crane Picks', 'Rigging', 'Aerial Lifts', 'Scissor Lifts',
  'Forklifts', 'Skid Steers', 'Excavators', 'Silica Exposure', 'Concrete Cutting', 'Confined Spaces',
  'Manholes/Vaults', 'Working at Heights', 'Occupational Heat Exposure',
];

const R = (step, hazard, control) => ({ step, hazard, control });

export const JHA_TEMPLATES = {
  'Excavation': [
    R('Locate & mark utilities', 'Contact with buried utilities', 'Call 811, pothole to verify, hand-dig within tolerance zone'),
    R('Protective system', 'Cave-in / entrapment', 'Slope, bench, shore or shield per soil class; competent-person inspection'),
    R('Access & egress', 'Fall / entrapment', 'Ladder or ramp within 25 ft of workers'),
    R('Spoil & load placement', 'Material falling in / surcharge', 'Keep spoils 2 ft+ from edge; barricade the opening'),
  ],
  'Trenching': [
    R('Protective system (5 ft+)', 'Cave-in', 'Slope/bench/shore/shield per soil classification'),
    R('Daily inspection', 'Changing conditions, water intrusion', 'Competent person inspects before entry and after rain'),
    R('Access / egress', 'Entrapment', 'Ladder/ramp every 25 ft of lateral travel'),
    R('Spoil placement', 'Struck-by / surcharge', 'Spoils 2 ft+ from edge'),
  ],
  'Potholing': [
    R('Utility locate', 'Utility strike', '811, vacuum excavation or hand tools within tolerance zone'),
    R('Vacuum / air-knife', 'Flying debris, noise', 'Face and hearing PPE; keep hands clear'),
    R('Open hole management', 'Fall / trip', 'Cover or barricade open holes'),
  ],
  'Directional Drilling': [
    R('Locate & pothole all crossings', 'Utility strike / inadvertent return', '811, pothole crossings, monitor returns'),
    R('Drill operation', 'Electrical strike / electrocution', 'Strike-alert system, insulating mats/gloves, grounding, clear personnel'),
    R('Rotating drill string', 'Caught-in / pinch', 'Stay clear of rotating pipe; no loose clothing'),
    R('Slurry management', 'Slip / environmental', 'Contain returns, manage spoils'),
  ],
  'Conduit Installation': [
    R('Material handling', 'Strain / pinch points', 'Team lift, proper body mechanics'),
    R('Cutting & solvent cementing', 'Chemical exposure', 'Ventilation, gloves, review SDS'),
    R('In-trench work', 'Cave-in', 'Follow trenching protective system'),
    R('Work in roadway', 'Struck-by', 'Approved traffic control plan'),
  ],
  'Wire Pulling': [
    R('Set up the pull', 'Pinch / caught-in / line whip', 'Communication, keep clear of bights, gloves'),
    R('Tensioning', 'Struck-by if line fails', 'Rated equipment, stay out of line-of-fire'),
    R('Lubricant use', 'Slip', 'Contain and clean spills'),
    R('Vault/manhole ends', 'Confined space', 'Follow confined space entry procedures'),
  ],
  'Energized Electrical Exposure': [
    R('Can it be de-energized?', 'Shock / arc flash', 'De-energize where feasible (LOTO); energized-work permit if not'),
    R('Approach boundaries', 'Shock', 'Maintain approach distances; qualified persons only'),
    R('Arc flash', 'Burns', 'Arc-rated PPE per incident energy; insulated tools'),
    R('Boundary control', 'Unqualified approach', 'Barricades and signage'),
  ],
  'De-energized Electrical Work': [
    R('Identify all sources', 'Unexpected energization', 'Apply LOTO to every source'),
    R('Verify zero energy', 'Stored/residual energy', 'Test before touch; discharge capacitors'),
    R('Grounding', 'Backfeed', 'Apply protective grounds'),
    R('Restore power', 'Premature energization', 'Clear personnel; remove locks per procedure'),
  ],
  'Lockout/Tagout': [
    R('Prepare', 'Unexpected startup', 'Identify all energy sources'),
    R('Isolate & lock', 'Release of energy', 'Apply locks/tags; each worker applies their own lock'),
    R('Verify', 'Residual energy', 'Try-out controls; test for zero energy'),
    R('Removal', 'Startup with personnel present', 'Only lock owner removes it; notify affected'),
  ],
  'Traffic Control': [
    R('Set up traffic control plan', 'Struck-by vehicle', 'Approved TCP; cones/signs/taper per MUTCD'),
    R('Flagging', 'Struck-by', 'Trained flaggers, hi-vis, paddle, planned escape route'),
    R('Work zone', 'Vehicle intrusion', 'Buffer space, barriers, spotter'),
    R('Night work', 'Low visibility', 'Lighting and reflective PPE'),
  ],
  'Street Lighting': [
    R('Set pole / foundation', 'Struck-by / tip-over', 'Rigging plan, exclusion zone'),
    R('Aerial work', 'Fall', 'Follow aerial lift procedures; harness'),
    R('Electrical connection', 'Shock', 'LOTO and verify de-energized'),
    R('Roadway work', 'Struck-by', 'Traffic control'),
  ],
  'Crane Picks': [
    R('Lift plan', 'Overload / tip-over', 'Rated capacity, load chart, ground bearing, outriggers'),
    R('Rigging', 'Dropped load', 'Inspect slings, proper hitch and angle'),
    R('Power lines', 'Electrocution', 'Maintain clearance, spotter, de-energize if possible'),
    R('Swing radius', 'Struck-by / crush', 'Barricade swing radius; use tag lines'),
  ],
  'Rigging': [
    R('Select rigging', 'Rigging failure', 'Rated slings/shackles; inspect; capacity for sling angle'),
    R('Attach load', 'Shift / drop', 'Proper hitch, account for center of gravity, tag lines'),
    R('Signaling', 'Miscommunication', 'One qualified signal person, standard signals'),
    R('Personnel position', 'Struck-by', 'Never under a suspended load'),
  ],
  'Aerial Lifts': [
    R('Pre-use inspection', 'Mechanical failure', 'Inspect before use'),
    R('Positioning', 'Tip-over', 'Level ground, outriggers, within capacity'),
    R('Fall protection', 'Ejection / fall', 'Harness with lanyard to platform anchor'),
    R('Overhead hazards', 'Contact / power line', 'Maintain clearance; use spotter'),
  ],
  'Scissor Lifts': [
    R('Pre-use inspection', 'Failure / missing guardrails', 'Check controls and guardrails'),
    R('Ground conditions', 'Tip-over', 'Firm level surface; do not drive elevated on slope; within capacity'),
    R('Occupant protection', 'Fall', 'Stay inside rails; gate closed'),
    R('Overhead / traffic', 'Struck-by', 'Clearance; barricade the base'),
  ],
  'Forklifts': [
    R('Pre-use inspection', 'Failure', 'Daily check'),
    R('Load handling', 'Tip-over', 'Within capacity, tilt back, carry low'),
    R('Travel', 'Struck-by / pedestrian', 'Horn, spotter, seatbelt, safe speed'),
    R('Pedestrians', 'Collision', 'Clear path; no riders'),
  ],
  'Skid Steers': [
    R('Pre-op inspection', 'Failure', 'Inspect before use'),
    R('Operation', 'Rollover / struck-by', 'Seatbelt, ROPS, spotter, keep bystanders clear'),
    R('Attachment service', 'Crush', 'Lower attachment; LOTO before servicing'),
    R('Utilities / grade', 'Strike / tip', 'Locate utilities; work on stable ground'),
  ],
  'Excavators': [
    R('Pre-op inspection', 'Failure', 'Inspect before use'),
    R('Swing & operation', 'Struck-by / crush', 'Barricade swing radius, spotter, no personnel in radius'),
    R('Underground utilities', 'Utility strike', '811, hand-dig within tolerance zone'),
    R('Overhead lines', 'Electrocution', 'Maintain clearance; use spotter'),
  ],
  'Silica Exposure': [
    R('Identify silica tasks', 'Respirable crystalline silica', 'Written exposure control plan; use Table 1 controls'),
    R('Dust control', 'Inhalation', 'Wet methods, HEPA vacuum, ventilation'),
    R('Respiratory protection', 'Overexposure', 'Respirator per plan, fit test, medical clearance'),
    R('Housekeeping', 'Re-entrainment of dust', 'No dry sweeping; HEPA clean-up'),
  ],
  'Concrete Cutting': [
    R('Silica dust', 'Respirable silica', 'Wet cut; follow silica exposure controls'),
    R('Saw operation', 'Kickback / blade contact', 'Guards in place, correct blade, two-hand control'),
    R('Embedded utilities', 'Strike conduit/rebar', 'Scan/locate (GPR) before cutting'),
    R('Noise & flying debris', 'Hearing / eye injury', 'Hearing, face and eye PPE'),
  ],
  'Confined Spaces': [
    R('Classify the space', 'Hazardous atmosphere / engulfment', 'Determine permit-required status; entry permit'),
    R('Atmospheric testing', 'IDLH atmosphere', 'Test before and continuously; ventilate'),
    R('Attendant & entry', 'Entrant emergency', 'Trained attendant, entrant, and entry supervisor'),
    R('Rescue', 'Failed rescue', 'Retrieval system and rescue plan; no unauthorized entry'),
  ],
  'Manholes/Vaults': [
    R('Classify space', 'Confined space hazards', 'Permit-required entry procedures'),
    R('Atmosphere', 'Gas / low oxygen', 'Test before and continuously; ventilate'),
    R('Roadway opening', 'Struck-by', 'Traffic control over the opening'),
    R('Electrical', 'Shock', 'LOTO / verify de-energized'),
  ],
  'Working at Heights': [
    R('Assess fall exposure', 'Fall', 'Fall protection at required height; guardrails or personal fall arrest'),
    R('Anchorage', 'PFAS failure', 'Rated anchor; inspect harness and lanyard'),
    R('Ladders', 'Fall', '3-point contact, correct angle, secure/tie off'),
    R('Falling objects', 'Struck-by below', 'Toe boards, tool tethers, exclusion zone'),
  ],
  'Occupational Heat Exposure': [
    R('Monitor conditions', 'Heat illness', 'Written heat-illness program; monitor heat index (per NV rule)'),
    R('Hydration & rest', 'Dehydration', 'Provide water, shade, and rest breaks'),
    R('Acclimatization', 'New / returning workers at risk', 'Gradual acclimatization schedule'),
    R('Recognize & respond', 'Heat stroke', 'Train on symptoms, buddy system, emergency response'),
  ],
};
